import { useState } from "react";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { X, UploadCloud, FileText, Download, CheckCircle2, AlertTriangle } from "lucide-react";

const SAMPLE_CSV = `Name,Email,Phone,Venue,Notes,Source
Alice Baker,alice@example.com,+447700900001,The Blue Room,Warm intro from Jamie,Referral
Bob Chen,bob@example.com,,Neon Nights,Interested in QR memberships,Cold email`;

export default function LeadsImportModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.(csv|txt)$/i.test(f.name)) {
      setError("Please choose a .csv file.");
      return;
    }
    setError("");
    setResult(null);
    setFile(f);
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/admin/leads/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      const total = (data.inserted || 0) + (data.updated || 0);
      toast.success(
        total ? `Imported ${data.inserted} new · updated ${data.updated}` : "No new leads imported"
      );
      onImported?.();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "affra-leads-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="leads-import-modal">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-zinc-900/50 backdrop-blur-sm"
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="p-6 border-b border-zinc-200 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-indigo-600">/ import</div>
            <h2 className="mt-1 font-display font-black text-2xl tracking-tight">Upload leads CSV</h2>
            <p className="mt-1 text-sm text-zinc-500">
              Bulk-add prospects into the <strong>New Lead</strong> column. Only <code>email</code> is required.
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="leads-import-close">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6 space-y-4">
          {/* Drop area */}
          <label
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDrag(false);
              pickFile(e.dataTransfer.files?.[0]);
            }}
            className={`block w-full cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition ${
              drag ? "border-indigo-500 bg-indigo-50" : "border-zinc-300 hover:border-zinc-500"
            }`}
            data-testid="leads-import-dropzone"
          >
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
              data-testid="leads-import-input"
            />
            <UploadCloud className="w-10 h-10 mx-auto text-zinc-400" />
            <div className="mt-3 font-medium">
              {file ? (
                <span className="text-zinc-900 flex items-center justify-center gap-2">
                  <FileText className="w-4 h-4" /> {file.name}
                </span>
              ) : (
                <span className="text-zinc-700">Drop a .csv here or click to browse</span>
              )}
            </div>
            <div className="mt-1 text-xs text-zinc-500">
              Headers we understand: <em>email, name, phone, venue, notes, source</em>
            </div>
          </label>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800 flex items-start gap-2" data-testid="leads-import-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {result && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm" data-testid="leads-import-result">
              <div className="flex items-start gap-2 text-emerald-800">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">
                    {result.inserted} new · {result.updated} updated
                  </div>
                  <div className="mt-1 text-emerald-700 text-xs">
                    Skipped: {result.skipped_no_email} row(s) without email · {result.invalid_email} invalid email(s).
                  </div>
                  {result.sample_inserted?.length > 0 && (
                    <div className="mt-1 text-emerald-700 text-xs">
                      New leads: {result.sample_inserted.join(", ")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={downloadSample}
            className="text-xs text-indigo-600 hover:text-indigo-500 inline-flex items-center gap-1"
            data-testid="leads-import-template"
          >
            <Download className="w-3.5 h-3.5" /> Download a template CSV
          </button>
        </div>

        <div className="p-4 border-t border-zinc-100 bg-zinc-50 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button
            onClick={submit}
            disabled={!file || uploading}
            className="rounded-full bg-zinc-900 hover:bg-zinc-800"
            data-testid="leads-import-submit"
          >
            {uploading ? "Uploading…" : "Import"}
          </Button>
        </div>
      </div>
    </div>
  );
}
